import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload, faChevronRight, faArrowUpRightFromSquare, faFolderOpen,
} from '@fortawesome/free-solid-svg-icons';
import { urlArquivo } from '../services/catalogo';
import { formatarTamanho, pluralizar } from '../utils/formatar';
import { iconePasta, iconeTipo } from '../utils/icones';

// ── destaque dos termos buscados (ignora acentos e maiúsculas) ───────────────

const DIACRITICOS = /[̀-ͯ]/g;
const base = (c) => c.normalize('NFD').replace(DIACRITICOS, '').toLowerCase();

export function Destaque({ texto, termos }) {
    if (!termos || !termos.length) return texto;

    // normaliza caractere a caractere para manter os índices alinhados ao original
    const mapa = Array.from(texto).map(base);
    if (mapa.some((c) => c.length !== 1)) return texto;
    const normal = mapa.join('');

    const marcado = new Array(texto.length).fill(false);
    termos.forEach((t) => {
        let i = normal.indexOf(t);
        while (i !== -1) {
            for (let k = i; k < i + t.length; k += 1) marcado[k] = true;
            i = normal.indexOf(t, i + t.length);
        }
    });

    const partes = [];
    let inicio = 0;
    for (let i = 1; i <= texto.length; i += 1) {
        if (i === texto.length || marcado[i] !== marcado[inicio]) {
            const trecho = texto.slice(inicio, i);
            partes.push(marcado[inicio] ? <mark key={inicio}>{trecho}</mark> : trecho);
            inicio = i;
        }
    }
    return partes;
}

// ── documento ───────────────────────────────────────────────────────────────

export function DocumentoItem({ doc, termos, mostrarCaminho = false }) {
    const { icone, cor } = iconeTipo(doc.tipo);

    return (
        <li className="doc">
            <span className={`doc__tipo doc__tipo--${cor}`} aria-hidden="true">
                <FontAwesomeIcon icon={icone} />
            </span>

            <div className="doc__corpo">
                {mostrarCaminho && (
                    <p className="doc__caminho">
                        {doc.caminho.map((p, i) => (
                            <React.Fragment key={p.id}>
                                {i > 0 && <FontAwesomeIcon icon={faChevronRight} className="doc__caminho-sep" />}
                                <Link to={`/pasta/${p.id}`}>{p.titulo}</Link>
                            </React.Fragment>
                        ))}
                    </p>
                )}
                <p className="doc__titulo"><Destaque texto={doc.titulo} termos={termos} /></p>
                {doc.descricao && (
                    <p className="doc__descricao"><Destaque texto={doc.descricao} termos={termos} /></p>
                )}
                <p className="doc__meta">{doc.tipo} · {formatarTamanho(doc.tamanho)}</p>
            </div>

            <a
                className="doc__baixar"
                href={urlArquivo(doc.url)}
                download={doc.arquivo}
                aria-label={`Baixar ${doc.titulo} (${doc.tipo}, ${formatarTamanho(doc.tamanho)})`}
            >
                <FontAwesomeIcon icon={faDownload} />
                <span>Baixar</span>
            </a>
        </li>
    );
}

export function ListaDocumentos({ documentos, termos, mostrarCaminho }) {
    return (
        <ul className="docs">
            {documentos.map((d) => (
                <DocumentoItem key={d.id} doc={d} termos={termos} mostrarCaminho={mostrarCaminho} />
            ))}
        </ul>
    );
}

// ── pasta ───────────────────────────────────────────────────────────────────

export function CardPasta({ pasta, termos }) {
    return (
        <Link to={`/pasta/${pasta.id}`} className={`hub-card hub-card--${pasta.cor}`}>
            <span className={`hub-card__icone hub-card__icone--${pasta.cor}`} aria-hidden="true">
                <FontAwesomeIcon icon={iconePasta(pasta.icone)} />
            </span>
            <div className="hub-card__corpo">
                <h3 className="hub-card__titulo"><Destaque texto={pasta.titulo} termos={termos} /></h3>
                {pasta.descricao && <p className="hub-card__desc">{pasta.descricao}</p>}
                <p className="hub-card__meta">{pluralizar(pasta.total, 'documento', 'documentos')}</p>
            </div>
            <FontAwesomeIcon icon={faChevronRight} className="hub-card__seta" />
        </Link>
    );
}

// ── links externos (INEA) ───────────────────────────────────────────────────

export function LinksUteis({ links }) {
    if (!links.length) return null;
    return (
        <section aria-labelledby="titulo-links" className="secao">
            <h2 id="titulo-links" className="secao__titulo">Acesso rápido</h2>
            <div className="links-uteis">
                {links.map((l) => (
                    <a
                        key={l.url}
                        className="link-externo"
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <div className="link-externo__corpo">
                            <span className="link-externo__titulo">{l.titulo}</span>
                            {l.descricao && <span className="link-externo__desc">{l.descricao}</span>}
                        </div>
                        <span className="link-externo__icone">
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            <span className="visually-hidden">(abre em nova aba)</span>
                        </span>
                    </a>
                ))}
            </div>
        </section>
    );
}

// ── estados ─────────────────────────────────────────────────────────────────

export function Vazio({ children }) {
    return (
        <div className="vazio" role="status">
            <FontAwesomeIcon icon={faFolderOpen} className="vazio__icone" />
            <p>{children}</p>
        </div>
    );
}
